import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import {
  Box,
  Fab,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import InsertEmoticonIcon from '@material-ui/icons/InsertEmoticon';
import SendIcon from '@material-ui/icons/Send';
import Picker, { EmojiStyle } from 'emoji-picker-react';
import {
  VirtuosoMessageListLicense,
  VirtuosoMessageList,
} from '@virtuoso.dev/message-list';
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import CustomChatRow from './CustomChatRow';
import CustomChatStickyFooter from './CustomChatStickyFooter';
import LoadingSpinner from '../ConversionCards/LoadingSpinner';
import { useAuthState } from '../../context/auth';
import config from '../../config';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  // constrain virtuoso so it scrolls inside Paper and composer stays visible
  virtWrapper: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  headerTitle: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  // message list area: grow to fill available Paper height (parent Modal defines height)
  list: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(2),
    paddingRight: 0,
    paddingBottom: 0,
    background: '#e8e8e8',
    position: 'relative',
  },
  messageRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(1.5),
    maxWidth: '85%',
  },
  bubble: {
    padding: theme.spacing(1),
    borderRadius: 12,
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  bubbleLeft: {
    background: '#fff',
    color: '#000',
    borderTopLeftRadius: 4,
  },
  bubbleRight: {
    background: theme.palette.primary.main,
    color: '#fff',
    borderTopRightRadius: 4,
  },
  metaTop: {
    fontSize: 12,
    marginBottom: 4,
    opacity: 0.9,
  },
  metaBottom: {
    fontSize: 11,
    opacity: 0.6,
    marginTop: 6,
    textAlign: 'right',
  },
  composer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  bannedBox: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    background: theme.palette.action.disabledBackground,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  inputBase: {
    flex: 1,
    padding: '6px 8px',
    background: theme.palette.background.paper,
    borderRadius: 8,
    maxHeight: 80,
    overflow: 'auto',
    fontSize: 15, // decreased font size for composer input (default)
  },
  // iOS Safari will zoom into inputs with font-size < 16px.
  // Apply a 16px font-size on iOS to prevent the automatic zoom.
  iosInput: {
    fontSize: 16,
  },
  emojiPickerBox: {
    position: 'absolute',
    bottom: 64,
    left: 16,
    zIndex: 1300,
  },
  charCounter: {
    fontSize: 12,
    opacity: 0.7,
    marginLeft: theme.spacing(1),
  },
}));

const Header = ({ context }) => {
  if (!context.loadingMore) return null;
  return (
    <div style={{ height: 30, textAlign: 'center' }}>
      <CircularProgress color="secondary" size={20} />
    </div>
  );
};

export default function CustomChat() {
  const classes = useStyles();
  const paperRef = useRef(null);
  const [paperHeight, setPaperHeight] = useState(null);
  const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);
  const [text, setText] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const MAX_CHARS = 300;
  const [sending, setSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [data, setData] = useState({ data: [] });
  const [socket, setSocket] = useState();
  const userIsAtBottomRef = useRef();
  const theme = useTheme();
  const matchesSM = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuthState();

  const addMessage = useCallback((message) => {
    setData((data) => ({
      data: [
        ...(userIsAtBottomRef.current) ? data.data.slice(-30) : data.data,
        message,
      ],
      scrollModifier: {
        type: 'auto-scroll-to-bottom',
        autoScroll: ({ atBottom, scrollInProgress }) => {
          if (atBottom || scrollInProgress) {
            return 'smooth';
          }
          return false;
        },
      },
    }));
  }, []);

  useEffect(() => {
    userIsAtBottomRef.current = true;
    const socket = io(config.customChatGlobal?.wsUrl, {
      reconnectionDelayMax: 10000,
      transports: ['websocket', 'polling'],
      auth: {
        token: localStorage.getItem('token'),
      },
      extraHeaders: {
        Authorization: localStorage.getItem('token'),
      },
    });
    socket.on('connect', () => {
      setSocket(socket);
    });

    socket.io.on('reconnect_attempt', () => setReconnecting(true));
    socket.io.on('reconnect', () => setReconnecting(false));

    socket.io.on('connect_error', (error) => {
      console.error(error);
    });

    socket.on('connect_error', (error) => {
      console.error(error);
    });

    socket.on('message', (message) => {
      addMessage(message);
    });

    socket.on('recentMessages', (recentMessages) => {
      setLoading(false);
      setData({
        data: recentMessages,
        scrollModifier: {
          type: 'item-location',
          location: {
            index: 'LAST',
            align: 'end',
          },
        },
      });
    });

    socket.on('banUser', () => setIsBanned(true));
    socket.on('unbanUser', () => setIsBanned(false));

    socket.on('deleteMessage', (messageToDelete) => {
      setData((data) => ({
        data: data.data.filter(({ externalId }) => messageToDelete.externalId !== externalId),
        scrollModifier: {
          type: 'items-change',
          behavior: 'smooth',
        },
      }));
    });

    // eslint-disable-next-line consistent-return
    return () => {
      socket.disconnect();
    };
  }, []);

  // load previous messages
  const onScroll = useCallback(async (location) => {
    if (userIsAtBottomRef.current !== location.isAtBottom) userIsAtBottomRef.current = location.isAtBottom;
    if (location.isAtBottom || location.listOffset < (matchesSM ? -10 : -5) || !socket || !data.data.length) return;

    setLoadingMore(true);
    const response = await socket.emitWithAck('getPreviousMessage', { firstExternalId: data.data[0].externalId });
    setData((data) => ({
      data: [
        ...response,
        ...data.data,
      ],
      scrollModifier: 'prepend',
    }));
    setLoadingMore(false);
  }, [data?.data, matchesSM]);

  // send message
  const handleSend = async () => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    if (trimmed.length > MAX_CHARS) return;
    try {
      setSending(true);
      const messageToSend = {
        message: trimmed,
        externalId: uuidv4(),
      };
      socket.emit('message', messageToSend);
      setData((data) => ({
        data: [
          ...data.data.slice(-30),
          {
            externalId: messageToSend.externalId,
            senderName: 'Du',
            userId: user.customerId,
            message: trimmed,
            timestamp: Date.now(),
          },
        ],
        scrollModifier: {
          type: 'auto-scroll-to-bottom',
          autoScroll: ({ atBottom, scrollInProgress }) => {
            if (atBottom || scrollInProgress) {
              return 'smooth';
            }
            return false;
          },
        },
      }));
      setText('');
      setShowPicker(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Send failed', err);
    } finally {
      setSending(false);
    }
  };

  const onEmojiClick = (emojiData) => {
    const next = (text || '') + (emojiData?.emoji || '');
    if (next.length > MAX_CHARS) return;
    setText(next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // measure paper height so picker can be constrained to it
  useLayoutEffect(() => {
    const measure = () => {
      if (paperRef.current) {
        const r = paperRef.current.getBoundingClientRect();
        setPaperHeight(Math.round(r.height) - 100);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [paperRef.current]);

  if (loading) {
    return (
      <Paper className={classes.root} elevation={3} style={{ height: '100%' }}>
        <LoadingSpinner />
      </Paper>
    );
  }

  if (reconnecting) {
    return (
      <Paper className={classes.root} elevation={3} style={{ height: '100%' }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
          <Box style={{ marginBottom: 12 }}>
            <LoadingSpinner />
          </Box>
          <Typography variant="body2" color="textSecondary">
            Verbindung wird wiederhergestellt…
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper ref={paperRef} className={classes.root} elevation={3} style={{ height: '100%' }}>
      <div className={classes.list}>
        <div className={`${classes.virtWrapper}  emoji-input`}>
          <VirtuosoMessageListLicense licenseKey="666ed440a2f35c7af98a41a4d4d4ae58TzoxMTc7RToxNzg5MjMzOTQ1NTEw">
            <VirtuosoMessageList
              data={data}
              style={{ flex: 1 }}
              onScroll={onScroll}
              context={{ loadingMore, customerId: user.customerId }}
              computeItemKey={({ data }) => data.id}
              ItemContent={CustomChatRow}
              StickyFooter={CustomChatStickyFooter}
              StickyHeader={Header}

            />
          </VirtuosoMessageListLicense>
        </div>
      </div>

      {isBanned ? (
        <Box className={classes.bannedBox}>
          <Typography variant="body2">
            Du wurdest vom Chat ausgeschlossen. Du kannst keine Nachrichten senden.
          </Typography>
        </Box>
      ) : (
        <Box className={classes.composer}>
          <IconButton aria-label="emoji" onClick={() => setShowPicker((s) => !s)} size="small">
            <InsertEmoticonIcon />
          </IconButton>
          <InputBase
            className={`${classes.inputBase} ${isIOS ? classes.iosInput : ''} emoji-input`}
            multiline
            rowsMax={6}
            placeholder="Schreibe eine Nachricht..."
            value={text}
            onChange={(e) => {
              if (e.target.value.length > MAX_CHARS) return;
              setText(e.target.value);
            }}
            onKeyDown={onKeyDown}
            inputProps={{ 'aria-label': 'message-input' }}
          />
          <Typography className={classes.charCounter} color="textSecondary">
            {text.length}/{MAX_CHARS}
          </Typography>
          <Fab
            size="small"
            color="primary"
            onClick={handleSend}
            disabled={sending || !text.trim()}
            aria-label="send"
          >
            <SendIcon fontSize="small" />
          </Fab>
        </Box>
      )}

      {showPicker ? (
        <Box className={`${classes.emojiPickerBox}`}>
          <Paper elevation={6}>
            <Picker
              onEmojiClick={onEmojiClick}
              skinTonesDisabled
              searchPlaceholder="Suche"
              searchDisabled
              height={paperHeight}
              emojiStyle={EmojiStyle.NATIVE}
              emojiVersion="13.0"
            />
          </Paper>
        </Box>
      ) : null}
    </Paper>
  );
}

CustomChat.defaultProps = {
  messages: [],
  currentUserId: 'me',
  groupName: 'Group',
  onSend: async () => {},
  onLoadPrevious: async () => [],
  hasMore: false,
};
